import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ArrowLeft from '@uber/base-icons/arrow-left';
import ArrowRight from '@uber/base-icons/arrow-right';
import Center from '@uber/base-icons/center';
import { useStyletron } from 'baseui';
import type { ButtonProps } from 'baseui/button';
import { Button, KIND, SHAPE, SIZE } from 'baseui/button';
import { SIZE as DIVIDER_SIZE, StyledDivider } from 'baseui/divider';
import { useAtom, useAtomValue } from 'jotai';
import useShortcut from '../../../image/labelling/providers/hooks/use-shortcut';
import { handleShortcutRegistrationError } from '../../../image/labelling/providers/utils/shortcut';
import { useMountEffect } from '../../hooks/use-mount-effect';
import { useReviewMode } from '../../providers/review-mode-providers';
import { isFocusedModeEnabledAtom, reviewAnnotationsAtom } from '../atoms';
import { REVIEW_ANNOTATION_TYPE } from '../constants';
import useAnalyticsForReviewSidePanel from '../hooks/useAnalyticsForReviewSidePanel';
import { SearchBar } from '../search-bar';
import { NavigationContainer, ObjectClassificationContainer, PanelHeader, SectionHeader as ObjectNavigationContainer, StyledHeading } from '../styled-component-overrides';
import { isClassificationAnnotationId } from '../utils';
import { ObjectClassification } from './object-classification';
import { TaskClassification } from './task-classification';
type Props = {
  annotationIds: string[];
  currentAnnotationIndex: number;
  selectedAnnotationId?: string;
};
const commonButtonProps: ButtonProps = {
  size: SIZE.mini,
  shape: SHAPE.circle,
  kind: KIND.tertiary
};
const buttonOverrides = {
  BaseButton: {
    style: ({
      $theme
    }) => ({
      marginRight: $theme.sizing.scale200
    })
  }
};
export const Classification = ({
  annotationIds,
  currentAnnotationIndex,
  selectedAnnotationId
}: Props) => {
  const [_, theme] = useStyletron();
  const reviewAnnotations = useAtomValue(reviewAnnotationsAtom);
  const {
    registerHotKeys
  } = useShortcut();
  const [isFocusedModeEnabled, setIsFocusedModeEnabled] = useAtom(isFocusedModeEnabledAtom);
  const {
    selectAnnotation
  } = useReviewMode();
  const {
    sendEventReviewPageChange
  } = useAnalyticsForReviewSidePanel();
  const previousPageRef = useRef(currentAnnotationIndex + 1);

  // Ensure currentAnnotationIndex is within bounds and the annotation exists
  const currentAnnotationId = useMemo(() => currentAnnotationIndex >= 0 && currentAnnotationIndex < annotationIds.length ? annotationIds[currentAnnotationIndex] : undefined, [currentAnnotationIndex, annotationIds]);
  const annotation = currentAnnotationId ? reviewAnnotations[currentAnnotationId] : undefined;
  const isClassificationType = isClassificationAnnotationId(currentAnnotationId);
  const disableFocusedMode = (id: string) => {
    setIsFocusedModeEnabled(isFocusedModeEnabled => {
      if (isClassificationAnnotationId(id) && isFocusedModeEnabled) {
        return false;
      }
      return isFocusedModeEnabled;
    });
  };
  const searchOptions = useMemo(() => annotationIds.map(id => ({
    id,
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    label: reviewAnnotations[id].name
  })), [annotationIds, reviewAnnotations]);
  const handleSearchChange = (value: string) => {
    const newIndex = annotationIds.findIndex(id => id === value);
    if (newIndex !== -1 && newIndex !== currentAnnotationIndex) {
      sendEventReviewPageChange({
        prevPage: previousPageRef.current,
        nextPage: newIndex + 1
      });
      previousPageRef.current = newIndex + 1;
    }
    disableFocusedMode(value);
    selectAnnotation?.(value);
  };
  const updateSelectedAnnotation = useCallback(() => {
    // User can manually deselect the annotation from stage, in that case we need to select the previously selected annotation
    if (!selectedAnnotationId && currentAnnotationId) {
      selectAnnotation?.(currentAnnotationId);
    }
  }, [currentAnnotationId, selectAnnotation, selectedAnnotationId]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const handleFocusModeToggle = useCallback(() => {
    setIsFocusedModeEnabled(isFocusedModeEnabled => {
      if (isFocusedModeEnabled) {
        return false;
      } else {
        updateSelectedAnnotation();
        return true;
      }
    });
  }, [updateSelectedAnnotation]);

  //This is required because, in review mode, an annotation should always be selected, even during the initial render.
  useMountEffect(() => {
    if (currentAnnotationId) {
      previousPageRef.current = currentAnnotationIndex + 1;
      selectAnnotation?.(currentAnnotationId);
    }
  });
  const handlePageChange = useCallback((newIndex: number) => {
    if (newIndex !== currentAnnotationIndex && newIndex >= 0 && newIndex < annotationIds.length) {
      sendEventReviewPageChange({
        prevPage: previousPageRef.current,
        nextPage: newIndex + 1
      });
      previousPageRef.current = newIndex + 1;
    }
  }, [currentAnnotationIndex, annotationIds.length, sendEventReviewPageChange]);
  useEffect(() => {
    const {
      removeHotKeys,
      errors
    } = registerHotKeys([{
      keys: ['z'],
      onAction: () => {
        if (currentAnnotationIndex > 0) {
          const newIndex = currentAnnotationIndex - 1;
          handlePageChange(newIndex);
          const previousAnnotationId = annotationIds[newIndex];
          if (!previousAnnotationId) {
            return;
          }
          disableFocusedMode(previousAnnotationId);
          selectAnnotation?.(previousAnnotationId);
        }
      }
    }, {
      keys: ['x'],
      onAction: () => {
        if (currentAnnotationIndex < annotationIds.length - 1) {
          const newIndex = currentAnnotationIndex + 1;
          handlePageChange(newIndex);
          const nextAnnotationId = annotationIds[newIndex];
          if (!nextAnnotationId) {
            return;
          }
          disableFocusedMode(nextAnnotationId);
          selectAnnotation?.(nextAnnotationId);
        }
      }
    }, {
      keys: ['Control', 'f'],
      onAction: () => {
        if (!isClassificationType) {
          handleFocusModeToggle();
        }
      }
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [registerHotKeys, selectAnnotation, currentAnnotationIndex, annotationIds, handleFocusModeToggle, handlePageChange, isClassificationType, isFocusedModeEnabled, setIsFocusedModeEnabled]);
  return <>
      <PanelHeader>
        <ObjectNavigationContainer>
          <StyledHeading>Objects</StyledHeading>
          <NavigationContainer>
            <Button {...commonButtonProps} shape={SHAPE.rectangular} kind={isFocusedModeEnabled && currentAnnotationId ? KIND.primary : KIND.secondary} onClick={handleFocusModeToggle} disabled={isClassificationType} overrides={buttonOverrides}>
              <Center />
            </Button>
            <Button {...commonButtonProps} disabled={currentAnnotationIndex === 0} onClick={() => {
            const newIndex = currentAnnotationIndex - 1;
            handlePageChange(newIndex);
            const previousAnnotationId = annotationIds[newIndex];
            if (!previousAnnotationId) {
              return;
            }
            disableFocusedMode(previousAnnotationId);
            selectAnnotation?.(previousAnnotationId);
          }}>
              <ArrowLeft />
            </Button>
            <Button {...commonButtonProps} disabled={currentAnnotationIndex === annotationIds.length - 1} onClick={() => {
            const newIndex = currentAnnotationIndex + 1;
            handlePageChange(newIndex);
            const nextAnnotationId = annotationIds[newIndex];
            if (!nextAnnotationId) {
              return;
            }
            disableFocusedMode(nextAnnotationId);
            selectAnnotation?.(nextAnnotationId);
          }}>
              <ArrowRight />
            </Button>
          </NavigationContainer>
        </ObjectNavigationContainer>
        <SearchBar placeholder="Label" onChange={handleSearchChange} options={searchOptions} currentValueIndex={Math.max(0, Math.min(currentAnnotationIndex, annotationIds.length - 1))} />
      </PanelHeader>

      <StyledDivider $size={DIVIDER_SIZE.cell} $style={{
      width: '100%',
      margin: `${theme.sizing.scale600} 0`
    }} />

      {annotation && <ObjectClassificationContainer>
          {annotation.type === REVIEW_ANNOTATION_TYPE.CLASSIFICATION ? <TaskClassification annotation={annotation} /> : <ObjectClassification annotation={annotation}
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string | undefined' is not assignable to type 'string'.
      annotationId={annotationIds[currentAnnotationIndex]} />}
        </ObjectClassificationContainer>}
    </>;
};